import type { Row, Table } from "@tanstack/react-table";
import {
  CircleOff,
  FileX,
  Loader2,
  MoreHorizontal,
  Search,
  UploadIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  SmbAttachmentStatus,
  type Transaction,
  TransactionSide,
} from "@/apis/gql/types";
import { DocumentUploadDropzone } from "@/components/shared/dropzones/document-upload-dropzone";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { useTenant } from "@/providers/session";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { InvoiceSuggestionCell } from "./invoice-suggestion-cell";
import { NoInvoiceRequiredCell } from "./no-invoice-required-cell";

type Props = {
  row: Row<Transaction>;
  transaction: Transaction;
  table: Table<Transaction>;
  onMarkAsReviewed: () => Promise<void>;
  onMarkAsLost: () => Promise<void>;
};

export const InvoiceCell = ({
  row,
  transaction,
  table,
  onMarkAsReviewed,
  onMarkAsLost,
}: Props) => {
  const { tenantId } = useTenant();
  const { t } = useTranslation();
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();
  const [dropDownOpen, setDropDownOpen] = useState(false);

  const handleSuccess = (invoiceIds: string[]) => {
    addInvoiceToTransaction({
      bankTxnIds: [transaction.bankTxnId],
      invoiceIds,
    });
  };

  if (
    transaction.smbAttachmentStatus ===
      SmbAttachmentStatus.ATTACHMENT_SUGGESTED &&
    transaction.invoices.length > 0
  ) {
    return (
      <div>
        <InvoiceSuggestionCell
          transaction={transaction}
          onMarkAsReviewed={onMarkAsReviewed}
        />
      </div>
    );
  }

  if (
    transaction.smbAttachmentStatus ===
    SmbAttachmentStatus.NOT_REQUIRED_SUGGESTED
  ) {
    return (
      <div>
        <NoInvoiceRequiredCell
          transaction={transaction}
          onMarkAsReviewed={onMarkAsReviewed}
        />
      </div>
    );
  }

  // Not always correct (e.g. credit notes), but good enough for most cases
  const invoiceType =
    transaction.side === TransactionSide.DEBIT
      ? InvoiceType.PAYABLE
      : InvoiceType.RECEIVABLE;

  return (
    <DocumentUploadDropzone
      tenantId={tenantId}
      clickUploadEnabled
      invoiceType={invoiceType}
      onSuccess={handleSuccess}
    >
      {({ isUploading }) => (
        <div className="flex items-center justify-center w-full px-2 my-2">
          <div className="flex flex-row items-center justify-center h-auto border-2 bg-gray-50 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 p-4 gap-2 w-full flex-wrap">
            <div className="flex flex-row items-center justify-center gap-2 text-gray-500">
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <UploadIcon className="w-5 h-5" />
              )}
              <p className="text-sm">
                <span className="font-semibold">
                  {isUploading ? t("uploading") : t("upload_here")}
                </span>
              </p>
            </div>
            <span className="text-xs text-muted-foreground">{t("or")}</span>
            <Button
              variant="outline"
              className="gap-2 h-8 px-3"
              onClick={(event) => handleRowClick(event, row)}
            >
              <Search className="w-4 h-4" />
              {t("transactions.search")}
            </Button>
            <span className="text-xs text-muted-foreground">{t("or")}</span>
            <Button
              variant="outline"
              className="gap-2 h-8 px-3"
              onClick={handleMarkAsReviewed}
            >
              <CircleOff className="w-4 h-4" />
              {t("transactions.actions.not_needed")}
            </Button>
            <DropdownMenu open={dropDownOpen} onOpenChange={setDropDownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-w-xs">
                <DropdownMenuItem className="gap-2" onClick={handleMarkAsLost}>
                  <div className="flex flex-row gap-2">
                    <div className="w-8">
                      <FileX className="w-4 h-4 mt-1" />
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium">
                        {t("transactions.actions.lost.title")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("transactions.actions.lost.description")}
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </DocumentUploadDropzone>
  );

  function handleRowClick(event: React.MouseEvent, row: Row<Transaction>) {
    event.preventDefault();
    event.stopPropagation();
    table.setRowSelection((prev) => ({
      [row.id]: !prev[row.id],
    }));
  }

  function handleMarkAsReviewed(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onMarkAsReviewed();
  }

  function handleMarkAsLost(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDropDownOpen(false);
    onMarkAsLost();
  }
};
